import React, { forwardRef } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Block, Heading } from 'suomifi-ui-components'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import {
  FormContainer,
  StyledTextInput,
  TextInputs,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import type {
  ForwardRefWrappedComponent,
  StepComponentProps,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import {
  type FirstErrorImperativeHandle,
  getErrorStatus,
  useFirstErrorImperativeHandle,
} from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import { useAetOrdererForm } from 'todistukset-ui/src/vtj/stores/aet-orderer-form.store'
import { useAetSignerForm } from 'todistukset-ui/src/vtj/stores/aet-signer-form.store'
import { useAetOrdererFormActions } from 'todistukset-ui/src/vtj/stores/actions/aet-orderer-form-actions'
import { useAetSignerFormActions } from 'todistukset-ui/src/vtj/stores/actions/aet-signer-form-actions'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import {
  validateContactEmail,
  validateContactPhoneNumber,
} from 'todistukset-common/src/vtj/validation'
import { normalizeTextInputWhitespace } from 'todistukset-common/src/vtj/form-sanitization.util'
import {
  AetOrdererUiForm,
  AetSignerUiForm,
} from 'todistukset-common/src/vtj/types/form-types.type'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'

type Form = AetOrdererUiForm | AetSignerUiForm

interface AetContactInfoProps
  extends Pick<StepComponentProps, 'titleId' | 'orderFlowName'> {
  useForm: typeof useAetOrdererForm | typeof useAetSignerForm
  useFormActions:
    | typeof useAetOrdererFormActions
    | typeof useAetSignerFormActions
}

type AetContactInfoComponent = ForwardRefWrappedComponent<
  FirstErrorImperativeHandle,
  AetContactInfoProps
>

const AetContactInfo: AetContactInfoComponent = observer(
  forwardRef(function AetContactInfo(
    { titleId, useForm, useFormActions, orderFlowName },
    ref
  ) {
    const { t } = useTranslation()

    const {
      steps: {
        [orderFlowName]: { showStepValidationErrors: showErrors },
      },
    } = useStore()

    const form = useForm()
    const { setContactPhone, setContactEmail } = useFormActions()

    const refs = useFirstErrorImperativeHandle(
      ref,
      form,
      getContactInfoErrors,
      ['phoneNumber', 'email']
    )

    const errorStatusParams = {
      form,
      showErrors,
      t,
    }

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="aetContactInfoMainTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetContactInfoMainDescription_paragraphs" />
        </Block>
        <Block variant="section">
          <TextInputs>
            <StyledTextInput
              id="contactInfoPhoneNumber"
              name="contactInfoPhoneNumber"
              labelText={t('aetContactInfoPhoneNumberFieldLabel')}
              type="tel"
              value={
                finalEditableFieldValue(form.contactInfo.phoneNumber) || ''
              }
              onChange={setContactPhone}
              onBlur={() =>
                setContactPhone(
                  normalizeTextInputWhitespace(
                    finalEditableFieldValue(form.contactInfo.phoneNumber) ?? ''
                  )
                )
              }
              fullWidth
              forwardedRef={refs.phoneNumber}
              {...errorStatus({
                ...errorStatusParams,
                field: 'phoneNumber',
              })}
            />
            <StyledTextInput
              id="contactInfoEmail"
              name="contactInfoEmail"
              labelText={t('aetContactInfoEmailFieldLabel')}
              type="email"
              value={finalEditableFieldValue(form.contactInfo.email) || ''}
              onChange={setContactEmail}
              /* This doesn't actually clear spaces from the input element, unsure if that's on
                 SFIDF or browser implementation of type="email" field. But at least the form
                 field gets trimmed regardless of what the browser does: */
              onBlur={() =>
                setContactEmail(
                  (finalEditableFieldValue(form.contactInfo.email) ?? '').trim()
                )
              }
              fullWidth
              forwardedRef={refs.email}
              {...errorStatus({
                ...errorStatusParams,
                field: 'email',
              })}
            />
          </TextInputs>
        </Block>
      </FormContainer>
    )
  })
)

export default AetContactInfo

const errorStatus = (params: {
  field: keyof ReturnType<typeof getContactInfoErrors>
  form: AetOrdererUiForm | AetSignerUiForm
  showErrors: boolean
  t: TFunction
}) => {
  return getErrorStatus({
    ...params,
    errors: {
      ...getContactInfoErrors(params.form),
    },
  })
}

const getContactInfoErrors = (form: Form) => {
  return {
    phoneNumber: validateContactPhoneNumber(
      form.contactInfo.phoneNumber?.ilmoitettuArvo
    ),
    email: validateContactEmail(form.contactInfo.email?.ilmoitettuArvo),
  }
}
